/**
 * The decisions taken on the changes made since a version of a report.
 *
 * Both calls return the standing decision on each section, so the caller never
 * has to merge what it sent into what it had.
 */

import { http, type RequestOptions } from '@/api/http'
import { readReview, type Decision, type Review, type StoredReview } from '@/domain/review'

interface Envelope {
  reviews: StoredReview[]
}

const url = (draftId: number, snapshotId: number): string =>
  `/reportDrafts/${draftId}/snapshots/${snapshotId}/reviews`

/** What has been decided about the changes read against this version. */
export async function listReviews(
  draftId: number,
  snapshotId: number,
  options: RequestOptions = {},
): Promise<Review[]> {
  const { reviews } = await http.get<Envelope>(url(draftId, snapshotId), options)
  return reviews.map(readReview)
}

/** Accept or reject changes. Refused once the report can no longer be edited. */
export async function decide(
  draftId: number,
  snapshotId: number,
  decisions: readonly { sectionId: string; decision: Decision; title: string }[],
  options: RequestOptions = {},
): Promise<Review[]> {
  const body = {
    decisions: decisions.map((one) => ({
      section_id: one.sectionId,
      decision: one.decision,
      title: one.title,
    })),
  }
  const { reviews } = await http.post<Envelope>(url(draftId, snapshotId), body, options)
  return reviews.map(readReview)
}
